import type React from 'react';
import { Fragment } from 'react';
import { Ellipsis, PageLink, PageLinkText, PaginationWrapper } from './Pagination.styles';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  /** Returns the href for a given page, e.g. (page) => `/carrier?page=${page}` */
  buildPageHref: (page: number) => string;
  /**
   * Keeps crawlers out of the paginated set without hiding it from users. For views whose URL
   * space is unbounded — a `?q=` search, where every term a bot invents has pages 2..N behind
   * it — the links are worth nothing to crawl and cost a Contentful query each.
   */
  isNofollow?: boolean;
}

// First and last page are always in the window, so crawlers reach the end of the list
// from any page rather than having to walk it one ?page=N at a time.
const getPageWindow = (currentPage: number, totalPages: number) => {
  const pages = new Set<number>([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);

  return Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
};

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, buildPageHref, isNofollow }) => {
  if (totalPages <= 1) return null;

  const pageWindow = getPageWindow(currentPage, totalPages);
  // `rel` takes a space-separated token list, so nofollow composes with prev/next rather
  // than replacing them.
  const buildRel = (relationship?: 'prev' | 'next') =>
    [relationship, isNofollow ? 'nofollow' : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <PaginationWrapper aria-label='pagination'>
      {currentPage > 1 && (
        <PageLink href={buildPageHref(currentPage - 1)} rel={buildRel('prev')}>
          <PageLinkText>Previous</PageLinkText>
        </PageLink>
      )}
      {pageWindow.map((page, index) => {
        const previousPage = pageWindow[index - 1];
        const hasGap = index > 0 && page - previousPage > 1;

        return (
          <Fragment key={page}>
            {hasGap && <Ellipsis>…</Ellipsis>}
            <PageLink
              href={buildPageHref(page)}
              rel={buildRel()}
              isActive={page === currentPage}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              <PageLinkText>{page}</PageLinkText>
            </PageLink>
          </Fragment>
        );
      })}
      {currentPage < totalPages && (
        <PageLink href={buildPageHref(currentPage + 1)} rel={buildRel('next')}>
          <PageLinkText>Next</PageLinkText>
        </PageLink>
      )}
    </PaginationWrapper>
  );
};

export default Pagination;
